import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Link,
  List,
  ListItemButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import FolderIcon from '@mui/icons-material/Folder';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import useStore from '../store/useStore';
import useIsMobile from '../hooks/useIsMobile';
import { pluralRu } from './BookMaterialsPicker';

// Materials catalogue → group materials. Browse the library folders or search
// across them (homework packs, Dimensions Math lesson pages, plans), tick files
// and get them back as group content items { type: 'file', text, url, note }.
//
// props: open, onClose, existingItems (group content, marks «уже в группе»), onAdd(items)

const KINDS = [
  { key: '', ru: 'Всё', en: 'All' },
  { key: 'homework', ru: 'Домашние задания', en: 'Homework' },
  { key: 'math', ru: 'Математика', en: 'Maths' },
  { key: 'plan', ru: 'Планы', en: 'Plans' },
];

const stripPdf = (name) => String(name || '').replace(/\.pdf$/i, '');

export default function CatalogPicker({
  open,
  onClose,
  existingItems = [],
  onAdd,
}) {
  const { language } = useStore();
  const en = language === 'en';
  const isMobile = useIsMobile();
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState('');
  const [trail, setTrail] = useState([]); // [{ _id, name }]
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [picked, setPicked] = useState(() => new Map()); // url → item

  const searching = query.trim().length > 0 || kind !== '';
  const have = useMemo(
    () => new Set((existingItems || []).map((c) => c.url).filter(Boolean)),
    [existingItems]
  );

  useEffect(() => {
    if (!open) return;
    setPicked(new Map());
    setError('');
  }, [open]);

  // Browse a folder, or search (debounced) when there is a query / kind.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const parentId = trail.length ? trail[trail.length - 1]._id : '';
    const url = searching
      ? `/api/materials/search?limit=150&q=${encodeURIComponent(query.trim())}${kind ? `&kind=${kind}` : ''}`
      : `/api/materials?parentId=${parentId}`;
    setLoading(true);
    const t = setTimeout(
      () => {
        fetch(url, { credentials: 'include' })
          .then((r) =>
            r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))
          )
          .then((data) => {
            if (cancelled) return;
            setItems(Array.isArray(data) ? data : []);
            setError('');
          })
          .catch((e) => {
            if (!cancelled) setError(e.message);
          })
          .finally(() => {
            if (!cancelled) setLoading(false);
          });
      },
      searching ? 250 : 0
    );
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [open, query, kind, trail, searching]);

  const folderPath = trail.map((f) => f.name).join(' › ');
  const toItem = (m) => ({
    type: 'file',
    text: stripPdf(m.name),
    url: m.url,
    note: m.folderPath != null ? m.folderPath : folderPath,
  });

  const toggle = (m) => {
    setPicked((prev) => {
      const next = new Map(prev);
      if (next.has(m.url)) next.delete(m.url);
      else next.set(m.url, toItem(m));
      return next;
    });
  };

  const files = items.filter((m) => m.type === 'file' || m.url);
  const folders = searching ? [] : items.filter((m) => m.type === 'folder');
  const selectable = files.filter((m) => !have.has(m.url));
  const allPicked =
    selectable.length > 0 && selectable.every((m) => picked.has(m.url));

  const add = () => {
    onAdd([...picked.values()]);
    onClose();
  };

  const n = picked.size;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={isMobile}
    >
      <DialogTitle sx={{ fontWeight: 800, pr: 6 }}>
        {en ? 'From the catalogue' : 'Из каталога'}
        <Typography variant="body2" color="text.secondary">
          {en
            ? 'Homework packs, maths book pages per lesson, plans'
            : 'Готовые домашние задания, страницы Dimensions Math по урокам, планы'}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 10, top: 10 }}
          aria-label={en ? 'Close' : 'Закрыть'}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: { xs: 1.5, sm: 2 } }}>
        <TextField
          fullWidth
          size="small"
          autoFocus={!isMobile}
          placeholder={
            en
              ? 'Search: KA 2-1, count to 5, surfaces, week 2…'
              : 'Поиск: KA 2-1, count to 5, surfaces, неделя 2…'
          }
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1.25 }}
        />
        <Stack
          direction="row"
          spacing={0.75}
          sx={{ mb: 1.25, flexWrap: 'wrap' }}
          useFlexGap
        >
          {KINDS.map((k) => (
            <Chip
              key={k.key || 'all'}
              size="small"
              label={en ? k.en : k.ru}
              color={kind === k.key ? 'primary' : 'default'}
              variant={kind === k.key ? 'filled' : 'outlined'}
              onClick={() => setKind(k.key)}
            />
          ))}
        </Stack>

        {!searching && (
          <Breadcrumbs
            separator={<ChevronRightIcon fontSize="small" />}
            sx={{ mb: 1, '& ol': { flexWrap: 'wrap' } }}
          >
            <Link
              component="button"
              underline="hover"
              onClick={() => setTrail([])}
              sx={{ fontWeight: trail.length ? 500 : 800 }}
            >
              {en ? 'Library' : 'Библиотека'}
            </Link>
            {trail.map((f, i) => (
              <Link
                key={f._id}
                component="button"
                underline="hover"
                onClick={() => setTrail(trail.slice(0, i + 1))}
                sx={{
                  fontWeight: i === trail.length - 1 ? 800 : 500,
                  textAlign: 'left',
                }}
              >
                {f.name}
              </Link>
            ))}
          </Breadcrumbs>
        )}

        {selectable.length > 1 && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Checkbox
              size="small"
              checked={allPicked}
              indeterminate={
                !allPicked && selectable.some((m) => picked.has(m.url))
              }
              onChange={() =>
                setPicked((prev) => {
                  const next = new Map(prev);
                  if (allPicked) selectable.forEach((m) => next.delete(m.url));
                  else selectable.forEach((m) => next.set(m.url, toItem(m)));
                  return next;
                })
              }
            />
            <Typography variant="body2" color="text.secondary">
              {en ? 'Select all here' : 'Выбрать все здесь'} (
              {selectable.length})
            </Typography>
          </Box>
        )}

        {error && (
          <Typography color="error" variant="body2" sx={{ py: 2 }}>
            {error}
          </Typography>
        )}
        {loading && !items.length ? (
          <Box sx={{ py: 5, textAlign: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        ) : !folders.length && !files.length ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ py: 4, textAlign: 'center' }}
          >
            {searching
              ? en
                ? 'Nothing found'
                : 'Ничего не найдено'
              : en
                ? 'This folder is empty'
                : 'Папка пуста'}
          </Typography>
        ) : (
          <List dense disablePadding sx={{ opacity: loading ? 0.6 : 1 }}>
            {folders.map((f) => (
              <ListItemButton
                key={f._id}
                onClick={() =>
                  setTrail([...trail, { _id: f._id, name: f.name }])
                }
                sx={{ borderRadius: 2, gap: 1.25 }}
              >
                <FolderIcon sx={{ color: '#E8A93D' }} />
                <Typography sx={{ flex: 1, fontWeight: 600, minWidth: 0 }}>
                  {f.name}
                </Typography>
                <ChevronRightIcon fontSize="small" color="action" />
              </ListItemButton>
            ))}
            {files.map((m) => {
              const inGroup = have.has(m.url);
              const isPdf = /\.pdf$/i.test(m.url || m.name || '');
              const Icon = isPdf ? PictureAsPdfIcon : InsertDriveFileIcon;
              return (
                <ListItemButton
                  key={m._id}
                  disabled={inGroup}
                  onClick={() => toggle(m)}
                  sx={{ borderRadius: 2, gap: 1, alignItems: 'flex-start' }}
                >
                  <Checkbox
                    size="small"
                    checked={inGroup || picked.has(m.url)}
                    tabIndex={-1}
                    sx={{ p: 0.5, mt: -0.25 }}
                  />
                  <Icon sx={{ color: 'primary.main', mt: 0.25 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, wordBreak: 'break-word' }}
                    >
                      {stripPdf(m.name)}
                    </Typography>
                    {(searching && m.folderPath) || inGroup ? (
                      <Typography variant="caption" color="text.secondary">
                        {inGroup
                          ? en
                            ? 'already in the group'
                            : 'уже в группе'
                          : m.folderPath}
                      </Typography>
                    ) : null}
                  </Box>
                  {m.url && (
                    <Tooltip title={en ? 'Open' : 'Открыть'}>
                      <IconButton
                        size="small"
                        href={m.url}
                        target="_blank"
                        rel="noopener"
                        onClick={(e) => e.stopPropagation()}
                        sx={{ pointerEvents: 'auto' }}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </ListItemButton>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.25 }}>
        <Button onClick={onClose}>{en ? 'Cancel' : 'Отмена'}</Button>
        <Button
          variant="contained"
          disableElevation
          disabled={!n}
          onClick={add}
        >
          {en
            ? `Add ${n || ''} ${n === 1 ? 'file' : 'files'}`
            : `Добавить ${n || ''} ${pluralRu(n || 5, ['файл', 'файла', 'файлов'])}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
